import { processTreeViewItems } from '@progress/kendo-react-treeview'
import React from 'react'
import { useSet } from 'react-use'

import {
  StyledTreeView,
  type StyledTreeViewProps,
} from '~/components/elements/StyledTreeView'
import { type Question as QuestionBase } from '~/gql/graphql'
import { QuestionOp } from '~/utils'

type Question = App.PickGraphQL<QuestionBase, 'question_id' | 'question_text'>

export type QuestionTreeViewProps<TQuestion extends Question> = {
  'data-testid'?: string
  defaultExpandedIds?: TQuestion['question_id'][]
  onItemClick?: (questionIds: TQuestion['question_id'][]) => void
  treeQuestions: (TQuestion & {
    items: Omit<TQuestion, 'mt_questions'>[] | null
  })[]
}

export const QuestionTreeView = <TQuestion extends Question>({
  'data-testid': dataTestId = 'components-features-question-tree-view',
  defaultExpandedIds,
  onItemClick,
  treeQuestions,
}: QuestionTreeViewProps<TQuestion>) => {
  const [expandIdSet, { toggle: toggleExpandIdSet }] = useSet<string>(
    new Set(defaultExpandedIds),
  )

  const treeQuestionsWithText = React.useMemo(
    () =>
      treeQuestions.map((treeQuestion) => ({
        ...treeQuestion,
        text: QuestionOp.toText(treeQuestion),
        items:
          treeQuestion.items?.map((item) => ({
            ...item,
            text: QuestionOp.toText(item),
          })) ?? null,
      })),
    [treeQuestions],
  )

  const data = React.useMemo(
    () =>
      processTreeViewItems(treeQuestionsWithText, {
        expand: { ids: [...expandIdSet], idField: 'question_id' },
      }),
    [expandIdSet, treeQuestionsWithText],
  )

  const handleExpandChange = React.useCallback<
    EH<StyledTreeViewProps, 'onExpandChange'>
  >(
    (event) => {
      toggleExpandIdSet(event.item.question_id)
    },
    [toggleExpandIdSet],
  )

  const handleItemClick = React.useCallback<
    EH<StyledTreeViewProps, 'onItemClick'>
  >(
    (event) => {
      const questionIds = event.item.items?.map(
        (subItem: TQuestion) => subItem.question_id,
      ) ?? [event.item.question_id]
      onItemClick?.(questionIds)
    },
    [onItemClick],
  )

  return (
    <StyledTreeView
      data={data}
      data-testid={dataTestId}
      expandIcons={true}
      onExpandChange={handleExpandChange}
      onItemClick={handleItemClick}
    />
  )
}
